import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getSkeletonForRoute } from '@components/ui/RouteSkeletons';

/**
 * Suspense fallback for route lazy loading: shows spinner on first load or share pages,
 * then route-specific skeleton to avoid layout shift.
 */
export function RouteLoadingFallback() {
  const location = useLocation();
  const [hasSeenSpinner, setHasSeenSpinner] = useState(
    () => typeof window !== 'undefined' && sessionStorage.getItem('route-spinner-shown') === 'true'
  );

  const isSharePage = location.pathname.startsWith('/share/');
  const showSpinner = isSharePage || !hasSeenSpinner;
  const path = location.pathname;

  useEffect(() => {
    if (showSpinner && !hasSeenSpinner && typeof window !== 'undefined') {
      sessionStorage.setItem('route-spinner-shown', 'true');
      setHasSeenSpinner(true);
    }
  }, [showSpinner, hasSeenSpinner]);

  if (showSpinner) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-dark animate-fade-in">
        <div className="flex flex-col items-center justify-center gap-4">
          <div className="relative">
            <div className="absolute inset-0 w-16 h-16 rounded-full bg-primary/10 animate-pulse-ring blur-md" />
            <div className="absolute inset-0 w-14 h-14 rounded-full border-[3px] border-primary/20 border-t-primary/60 animate-spin-slow" />
            <div
              className="relative w-12 h-12 rounded-full border-4 border-transparent bg-gradient-to-r from-primary via-primary/60 to-primary animate-gradient-spin"
              style={{
                background:
                  'conic-gradient(from 0deg, rgba(48, 216, 112, 0.2), rgba(0, 255, 97, 0.8), rgba(48, 216, 112, 0.2))',
                mask: 'radial-gradient(farthest-side, transparent calc(100% - 4px), white calc(100% - 4px))',
                WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 4px), white calc(100% - 4px))',
              }}
            />
          </div>
          <div className="flex flex-col items-center gap-2">
            <p className="text-white/70 text-sm font-inter font-medium bg-gradient-to-r from-white via-primary/80 to-white bg-clip-text text-transparent bg-[length:200%_100%] animate-shimmer-loading">
              Loading...
            </p>
            <div className="flex gap-1">
              <div className="w-1 h-1 bg-primary rounded-full animate-bounce [animation-delay:-0.3s]" />
              <div className="w-1 h-1 bg-primary rounded-full animate-bounce [animation-delay:-0.15s]" />
              <div className="w-1 h-1 bg-primary rounded-full animate-bounce" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <main
      className="relative ml-0 pt-[66px] sm:pt-[71px] md:pt-[76px] lg:pt-[81px] xl:pt-[86px] px-2 sm:px-4 md:px-6 lg:px-12 xl:px-16 2xl:px-20 pb-20 md:pb-24 min-w-0 w-full overflow-x-hidden bg-gradient-to-br from-[#050510] via-[#0a0a15] via-[#0f0c29] via-[#141428] to-[#16213e] sidebar-offset-main"
      style={{ minWidth: '200px' }}
    >
      <div className="w-full min-w-0 relative">{getSkeletonForRoute(path)}</div>
    </main>
  );
}
